import { z } from 'zod';
import { recordingTrimSchema } from './preview-recording-trim';

const shortText = z.string().max(200);
const variables = z.record(
  z.string().min(1).max(100).refine(key => !['__proto__', 'prototype', 'constructor'].includes(key)),
  z.string().max(2000),
).refine(value => Object.keys(value).length <= 100);

/** Only test inputs and display preferences belong here, never runtime/config objects. */
const previewLinkSchema = z.object({
  view: z.enum(['fullscreen', 'demo']).default('demo'),
  recording: z.boolean().default(false),
  recordingTrim: recordingTrimSchema.optional(),
  activity: z.boolean().default(true),
  sms: z.boolean().default(false),
  telephony: z.boolean().default(false),
  channel: z.enum(['voice', 'chat']).default('voice'),
  chatEvents: z.boolean().default(false),
  variables: variables.default({}),
  activitySettings: z.object({
    callerLabel: z.string().max(80).default(''),
    agentLabel: z.string().max(80).default(''),
    timestampPrecision: z.enum(['minutes', 'seconds']).default('seconds'),
    showActionsInline: z.boolean().default(false),
  }).prefault({}),
  telephonySettings: z.object({
    integrationId: shortText.default(''),
    destination: shortText.default(''),
    contactFlowId: shortText.default(''),
    phoneLabel: shortText.default(''),
  }).prefault({}),
});

export type PreviewLinkSettings = z.infer<typeof previewLinkSchema>;
export const PREVIEW_LINK_PREFIX = '#preview=v1.';
export const MAX_PREVIEW_HASH_LENGTH = 32_768;

function normalizeSettings(value: unknown): PreviewLinkSettings {
  const settings = previewLinkSchema.parse(value);
  // Provider telephony owns SMS delivery and always previews a voice call.
  if (settings.telephony) {
    settings.sms = false;
    settings.channel = 'voice';
  }
  if (settings.channel === 'chat') settings.view = 'fullscreen';
  return settings;
}

export function encodePreviewHash(value: PreviewLinkSettings): string {
  let settings: PreviewLinkSettings;
  try { settings = normalizeSettings(value); } catch {
    throw new Error('Some Preview settings cannot fit in a shared link. Check the labels and template variables.');
  }
  const json = JSON.stringify(settings);
  const bytes = new TextEncoder().encode(json);
  const hash = PREVIEW_LINK_PREFIX + btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''))
    .replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
  if (hash.length > MAX_PREVIEW_HASH_LENGTH) throw new Error('Preview settings are too large to share in a link. Shorten the labels or variables.');
  return hash;
}

/** Invalid/future links behave like an ordinary editor URL. No partial restoration. */
export function decodePreviewHash(hash: string): PreviewLinkSettings | null {
  if (!hash.startsWith(PREVIEW_LINK_PREFIX) || hash.length > MAX_PREVIEW_HASH_LENGTH) return null;
  try {
    const encoded = hash.slice(PREVIEW_LINK_PREFIX.length);
    if (!/^[a-zA-Z0-9_-]+$/.test(encoded)) return null;
    const bytes = Uint8Array.from(atob(encoded.replaceAll('-', '+').replaceAll('_', '/')), char => char.charCodeAt(0));
    return normalizeSettings(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes), (key, value) => {
      if (['__proto__', 'prototype', 'constructor'].includes(key)) throw new Error('Invalid preview key');
      return value;
    }));
  } catch {
    return null;
  }
}

export function previewShareUrl(href: string, settings: PreviewLinkSettings): string {
  const url = new URL(href);
  // A share starts a new preview, not a previous Studio demo run.
  for (const key of ['demo_design', 'demo_component', 'demo_run', 'mockCallerPacing', 'mode', 'tab']) url.searchParams.delete(key);
  const variables = Object.fromEntries(Object.entries(settings.variables)
    .filter(([key]) => !['demo_design_id', 'demo_run_id'].includes(key)));
  url.hash = encodePreviewHash({ ...settings, variables });
  return url.href;
}

export function clearPreviewHash(): void {
  const url = new URL(window.location.href);
  if (!url.hash.startsWith('#preview=')) return;
  url.hash = '';
  window.history.replaceState(window.history.state, '', url);
}

/** Redirect responses inherit the original fragment; keep it local through login. */
export function previewLoginDestination(redirect: string, hash: string): string {
  try {
    const path = decodeURIComponent(redirect.split('?')[0]);
    if (!/^\/agents\/\d+(?::(?:draft|v?\d+))?$/.test(path) || !decodePreviewHash(hash)) return redirect;
    return redirect.split('#')[0] + hash;
  } catch { return redirect; }
}
